import { print } from 'graphql';
import config from '../config';
import RefreshTokenMutation from '../graphql/RefreshToken';
import { getAccessToken, setAccessToken } from './accessTokenStore';

// RefreshTokenMutation is the single source of truth for this query. It's a parsed AST, not a
// wire-format string, because it's normally handed straight to Apollo Client — print() serializes
// it back to text for the plain fetch() below.
const REFRESH_TOKEN_QUERY = print(RefreshTokenMutation);

/** Thrown by the refresh flow; `transient` tells a caller whether retrying might help. */
class RefreshTokenError extends Error {
  /** @param {{ transient: boolean }} options */
  constructor(message, { transient }) {
    super(message);
    this.name = 'RefreshTokenError';
    this.transient = transient;
  }
}

// Plain fetch, deliberately outside the Apollo link chain: this call must never pass through
// errorLink, or a failed refresh would try to refresh itself.
async function callRefreshMutation(refresh_token) {
  let response;
  try {
    response = await fetch(config.API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: REFRESH_TOKEN_QUERY,
        variables: { refresh_token },
      }),
    });
  } catch (networkError) {
    throw new RefreshTokenError('Network error while refreshing token', { transient: true });
  }

  let json;
  try {
    json = await response.json();
  } catch (parseError) {
    throw new RefreshTokenError('Malformed refresh token response', { transient: true });
  }

  const { data, errors } = json;

  if (errors) {
    // The mutation ran and the server explicitly said no (e.g. "Session expired") — the refresh
    // token itself is dead, not a transient failure worth retrying.
    throw new RefreshTokenError(errors[0]?.message || 'Refresh token rejected', {
      transient: false,
    });
  }

  if (!data?.refresh_token?.access_token) {
    throw new RefreshTokenError('Refresh token response missing access_token', {
      transient: true,
    });
  }

  return data.refresh_token;
}

async function runRefresh(staleAccessToken) {
  const currentAccessToken = getAccessToken();
  if (currentAccessToken && currentAccessToken !== staleAccessToken) {
    return currentAccessToken;
  }

  const refresh_token = localStorage.getItem('@refresh_token');
  if (!refresh_token) {
    throw new RefreshTokenError('No refresh token available', { transient: false });
  }

  const tokens = await callRefreshMutation(refresh_token);
  setAccessToken(tokens.access_token);
  localStorage.setItem('@refresh_token', tokens.refresh_token);
  return tokens.access_token;
}

const LOCK_NAME = 'fcportal-refresh-token';

// Same-tab fallback for when Web Locks isn't available: dedupes concurrent callers onto one
// in-flight refresh instead of each spending the same single-use refresh token independently.
// Web Locks doesn't need this — it already serializes callers itself.
let inFlightRefresh = null;

function refreshWithoutLock(staleAccessToken) {
  if (inFlightRefresh) {
    return inFlightRefresh;
  }

  inFlightRefresh = runRefresh(staleAccessToken).finally(() => {
    inFlightRefresh = null;
  });

  return inFlightRefresh;
}

/**
 * Refreshes the access token, coordinated across tabs via Web Locks so two tabs hitting an
 * expired token at once don't both spend the same single-use, rotating refresh token. Falls back
 * to same-tab-only dedupe where Web Locks is unavailable (non-secure contexts, e.g. dev/stg's
 * plain HTTP).
 *
 * @param {string | null} staleAccessToken - token the caller last had (`null` at bootstrap)
 * @returns {Promise<string>} the current (possibly just-refreshed) access token
 */
export default async function refreshAccessToken(staleAccessToken) {
  if (!navigator.locks) {
    return refreshWithoutLock(staleAccessToken);
  }

  return navigator.locks.request(LOCK_NAME, () => runRefresh(staleAccessToken));
}
